import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type UIEvent,
} from 'react';

import type {
  AttendanceStatus,
  ChoirAttendanceMatrix,
  ChoirBrief,
  MatrixMember,
  MatrixMonths,
  MatrixSession,
  MemberRow,
  SessionKind,
  VoicePart,
} from '@contracts/index';

import { ApiError, api } from '../api';
import { useBackKey } from '../backKey';
import {
  formatDay,
  formatLongDate,
  monthLabels,
  plural,
  voicePartLetter,
  voicePartName,
  words,
} from '../format';
import { useHome } from '../home';
import { ChevronDownIcon, ChevronRightIcon, GridIcon } from '../icons';
import MembersCard from './MembersCard';
import MessagesScreen from './MessagesScreen';
import PersonScreen from './PersonScreen';
import Push from './Push';
import SermonsScreen from './SermonsScreen';
import SongsScreen from './SongsScreen';
import Sheet from './Sheet';
import Unfold from './Unfold';
import { Failed, Settling, Skeleton } from './States';
import UndoBar, { type Done } from './UndoBar';

/**
 * **The choir tab's hub, from `legacy/frontend/src/screens/ChoirScreen.tsx` — a region port,
 * built up task by task** (`docs/specs/features/F-016/plan.md` T7, T8, T10).
 *
 * What is here is the hub as T7, T8 and T10 draw it: `whoKeeps` and the comment over it
 * (`:97-116`), the page state narrowed (to two pages then, four since F-020 T4), `useHome`,
 * the hub's one request (`:722-734`), `pushed()` (`:771-785`), the keepers line (`:891-901`),
 * and the roster card under
 * it (`:1008-1017`) with `isRegent` (`:651`) as its `readOnly`. The card makes its own
 * `GET /choir/members` beside the hub's — `FND-510`, `KEEP`. T10 brought `person` (`:671-672`),
 * the row's `onOpen` (`:1012-1015`) and the `person` arm (`:787-796`, `:812`), which pushes
 * `PersonScreen` keyed by the person.
 *
 * F-020 T3 brought the statistics door and its page: `paused` with its doc comment
 * (`:681-682`), written in the hub request's success branch (`:735`, as `members` rather than
 * `members.value` until F-023 T10 made it legacy's), the `stats` arm (`:833-835`) pushing `ChoirStatsScreen`, and the tile with
 * its comment (`:920-934`), after the keepers line.
 *
 * **C-017 (`FND-1612`, 2026-10-08, after the cutover) took the statistics door out again**: the
 * tile with its comment, the `stats` member of `Page` and its arm, and `paused` with its write.
 * Everything the page showed is in the table or in the roster's «В паузе», and the owner chose
 * one door. `scripts/ported-partial.txt` holds the regions left.
 *
 * F-020 T4 brought the attendance door and its page: `PERIODS` (`:71-78`); `MatrixPanel` with its
 * doc comment and the state its controls need (`:129-143`), `load` and its effect (`:170-184`),
 * and its JSX up to the grid (`:252-352`) — the caption, the period control, the kind segment,
 * the fold, the two states that are not a grid and the empty sentence; the component's doc
 * comment (`:618-635`); `useBackKey` for the page with its comment (`:757-760`); the `attendance`
 * arm (`:856-868`); and the tile with its comment (`:903-911`, `:917-918`), above the statistics
 * tile. The comment over the two non-grid states (`:337-339`) says a failed switch leaves the
 * table on screen alone; it does not, since `load` clears the grid first (`:173`) and a failed
 * switch shows `Failed` — **`FND-633`, `KEEP`**, «KEEP, с заметкой»: the comment stays verbatim
 * and this sentence is the note.
 *
 * F-020 T5 brought the grid: `LEGEND` (`:51-59`), and `PART_ORDER`, `groups` and `share`
 * (`:80-95`), round `PERIODS`; `axisRef` and `syncAxis` with their comment (`:159-168`); the
 * totals, `barred`, `axisMonths` and `pinClass` (`:238-250`); and the grid branch (`:354-537`) —
 * the legend, the frame, the axis, the shelves, the rows and their cells, the fade, the hint and
 * «Итоги периода». The signature destructured `myMemberId` alone until T7 brought `isRegent`'s
 * reader, since `noUnusedParameters` refuses a binding nothing reads. The editable cell is drawn without its `onClick` (`:489-492`), because the handler sets
 * the state the sheet reads and both arrive in T6 — a button that answers no press until then,
 * which is this task's one seam.
 *
 * F-020 T6 brought the edit and closed that seam: `VERDICTS` with its comment (`:61-69`), the
 * state `editing` and `saving` (`:144-150`) and `editFailure` (`:158`), `refresh` with its comment
 * (`:186-198`), `setCell` (`:219-236`), the cell's `onClick` (`:489-492`) and the `Sheet` with its
 * comment (`:565-613`). The write reaches a gathering that may still be running, and nothing polls
 * — **`FND-625`, `KEEP`**; the refresh after a successful write re-asks `GET /choir/attendance`
 * without blanking the grid — **`FND-627`, `KEEP`**. Three lines are ported as written and held by
 * review, not by a test (AC-49), each measured on 2026-09-21 by applying the mutation and running
 * every case under `src/screens/`, which stayed green: removing `if (editing === null) return;`
 * from `setCell`, replacing `?? -1` in the selected verdict's index with `!`, and removing
 * `setEditFailure(null)` from the cell's tap. No path reaches the first two — the sheet opens only
 * inside the grid branch, and the controls that call `load()` sit behind the modal — and closing
 * already clears the failure the third clears, the sheet being modal.
 *
 * F-020 T7 brought the export — **`FND-631`, `FIX`**, «Едет с F-020», which moved it into this
 * slice and changed nothing on screen: `MatrixPanel`'s signature as legacy `:132` has it, with
 * `isRegent`; the state with its comment (`:151-157`); `send()` with its comment (`:200-217`),
 * posting `api.sendJournal(months)` with the window on screen; and the block with its comment
 * (`:538-560`), the regent's only, inside the grid branch. One line is ported as written and held
 * by review, not by a test (AC-43), measured on 2026-09-21 by applying the mutation and running
 * every case under `src/screens/`, which stayed green: removing `!sending` from `sent && !sending`.
 * `send()` clears `sent` in the batch that sets `sending` (`:204-206`), so no render has both.
 *
 * F-020 T8 brought the badge back with its tile: `needsReview` with its doc comment (`:656-657`),
 * the effect that counts it (`:705-718`) and the badge (`:912-916`), inside the attendance tile.
 * A regent asks `GET /session/history` on the hub's first mount and on every page change after
 * it — **`FND-510`, `KEEP`**, carried as it is. The effect reads `api.history()` where legacy
 * reads `api.sessionHistory()` (`:712`), so that line is ours; the note beside the comment over it
 * (`:709-711`), which says «the whole history» where the server counts fourteen days, sits on the
 * same line's seam — **`FND-511`, `KEEP`**, «Только комментарий; число считает сервер».
 *
 * F-023 T10 brought the songs door and its page: `songCount` with its comment (`:660-661`); the
 * hub's request as legacy's `Promise.allSettled` over `api.choirSongs()` and `api.choirMembers()`
 * (`:727-729`, `:731-744`), each result written only if fulfilled, with legacy's dependency list
 * `[choir.member_id, isRegent, page]` (`:753-755`) where this file had `[choir.member_id, page]`
 * and no finding allowed it (F-023 spec §1.1 item 5); the `songs` arm (`:841-843`) pushing
 * `SongsScreen`; and the comment over the three doors (`:936-951`), verbatim, with the tile under
 * it (`:965-974`), for everybody, after the statistics tile. The tile's sub-line is narrower than
 * the server — **`FND-1548`, `KEEP`**. The `try` this file wrapped round the roster alone went
 * with its two comments; they were ours.
 *
 * F-024 T14 brought the programme door and its page: `service` with its doc comment
 * (`:658-659`), before `songCount`; `api.nextService()` first in the hub's `Promise.allSettled`,
 * destructured as legacy's `[next, songs, members]`, and its write only if fulfilled (`:725-730`),
 * so the request is legacy's `:724-744` whole; the `program` arm (`:849-854`) pushing
 * `ProgramScreen` for the service ahead, which falls through to the hub while there is none; and
 * the tile (`:952-963`), for everybody, the first of the three doors under their comment. The
 * server alone decides which service is next — **`FND-1566`, `KEEP`** — and the client applies no
 * clock to it.
 *
 * **C-013 (`FND-1608`, 2026-10-07, after the cutover) took all of that out again**: the tile, the
 * `program` arm, `service` and the request. Each service's card opens its programme, and the owner
 * chose to keep that one road. `scripts/ported-partial.txt` holds the regions left.
 *
 * F-026 brought the messages door and its page: the regent's tile (`:976-985`), its count (`:662`,
 * `:745-752`) — asked for by a regent's hub alone, after the other three requests have settled —
 * and the `messages` page (`:126`, `:845-847`), which pushes `MessagesScreen` whatever the role.
 * F-025 brought «Проповеди» (call 8 made it `Ship`): the regent's tile with both its comments
 * (`:987-1003`) and the `sermons` page (`:837-839`), which pushes `SermonsScreen`. It reads the
 * keepers the hub already holds and asks for nothing. Merged 2026-09-21: F-025 landed first and
 * F-020 re-measured. Merged 2026-09-30: F-024 landed first and F-026 re-measured. With both, D1
 * (`FND-501`) removes no tile any more: the six doors of the old hub are all drawn.
 *
 * F-017 T11 brought the band: `rotated` (`:683-689`), the card's `onGone` (`:813-819`), and the
 * band itself, first on the hub, with its comment (`:874-889`). The code is the deletion answer's
 * `invite_code` alone, and the band gives nothing to do with it — **`FND-542`, `KEEP`**. It lives
 * until this screen unmounts: no page change, report or re-read clears it.
 *
 * F-017 T8 brought the report: `done`, `acts` and `report` (`:690-703`), `setDone(null)` on every
 * page change with its comment (`:762-769`), the card's `onDone={report}` (`:820`) with the bar
 * under it and its comment (`:822-828`) in the arm's fragment, and the hub's bar, last
 * (`:1018-1019`). Both bars read the one `done`. What that does when the card is left inside the
 * five seconds — two bars for the slide, then one on the hub with no timer — is **`FND-553`,
 * `KEEP`**, and the undo is the opposite call from the row as drawn (**`FND-530`, `KEEP`**).
 *
 * `whoKeeps` is ported with the comment that says it agrees with the server, and it does not:
 * the server's fallback is the head regent, this one is the regent who joined first
 * (`backend/app/domain/roles.py:83-92`). **`FND-503`, `KEEP`** — «Паритет: правка меняет
 * видимое имя».
 *
 * The regions of the old file this one reproduces line for line are on
 * `scripts/ported-partial.txt`.
 */

/** The four things a cell can say, in the order the legend lists them. */
/* The app's one attendance vocabulary - see «Attendance vocabulary» in
   `DESIGN.md`. Nouns rather than participles, which agree in gender. */
const LEGEND: [string, string][] = [
  ['on_time', 'вовремя'],
  ['late', 'опоздание'],
  ['absent', 'пропуск'],
  ['none', 'нет данных'],
];

/* The three verdicts a cell can be changed to, in the words the roster row uses
   for the same job. A fourth — «нет данных» — is not offered: a cell with no
   mark is a person nobody recorded, and «unrecord somebody» is not an act this
   app has. Clearing a mark is `absent`, which is what closing writes anyway. */
const VERDICTS: [AttendanceStatus, string][] = [
  ['on_time', 'Вовремя'],
  ['late', 'Опоздание'],
  ['absent', 'Пропуск'],
];

/* The three windows, in the words the button says them in. «Полгода» rather
   than «6 месяцев»: it is the word a person uses, and the row is read at a
   glance. The figures they produce are the server's - see MatrixMonths there. */
const PERIODS: [MatrixMonths, string][] = [
  [3, '3 месяца'],
  [6, 'полгода'],
  [12, 'год'],
];

/** The order the voice parts are grouped in, which is the order a choir sits. */
const PART_ORDER: (VoicePart | null)[] = ['soprano', 'alto', 'tenor', 'bass', null];

/** The members in the order a choir sits, gathered under their own part. */
function groups(matrix: ChoirAttendanceMatrix) {
  return PART_ORDER.map((part) => ({
    part,
    people: matrix.members.filter((member) => member.voice_part === part),
    totals: matrix.by_voice_part.find((row) => row.voice_part === part),
  })).filter((group) => group.people.length > 0);
}

/** A share as a whole percent, or a dash where there is nothing to divide. */
function share(attended: number, total: number): string {
  return total === 0 ? '—' : `${Math.round((attended / total) * 100)}%`;
}

/**
 * Who keeps each of the two delegated functions, read off the roster.
 *
 * The holder of the flag, and the regent where nobody holds it — which is what
 * «не поручил — делает регент» looks like to somebody reading the line. A choir
 * with two regents names the one who has been in it longest, because a sentence
 * has to name one person; the roster arrives sorted by name, so `joined_at`
 * decides it here the same way it does on the server.
 */
function whoKeeps(members: MemberRow[]): { program: string; sermons: string } {
  const active = members.filter((row) => row.standing === 'active');
  const regent = active
    .filter((row) => row.role === 'regent')
    .sort((a, b) => a.joined_at.localeCompare(b.joined_at))[0];
  const fallback = regent?.display_name ?? 'регент';
  return {
    program: active.find((row) => row.is_program_admin)?.display_name ?? fallback,
    sermons: active.find((row) => row.is_sermon_admin)?.display_name ?? fallback,
  };
}

/* All eight of the old ones (`:119-127`), in their order there. F-020 T3 brought
   `stats` and T4 `attendance`; F-025 brought `sermons`, F-023 `songs`, F-026
   `messages` and F-024 `program`. C-013 took `program` out again, and C-017
   `stats`. */
/** Which of the choir's own screens is showing. */
type Page =
  | 'choir'
  | 'attendance'
  | 'person'
  | 'sermons'
  | 'songs'
  | 'messages';

/**
 * The table, without a head of its own: the page above it carries the title.
 */
function MatrixPanel({ myMemberId, isRegent }: { myMemberId: number; isRegent: boolean }) {
  const [matrix, setMatrix] = useState<ChoirAttendanceMatrix | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  /* One table per kind rather than two screens: it is the same grid asking the
     same question about a different set of gatherings, and the two would go on
     being edited in parallel forever. */
  const [kind, setKind] = useState<SessionKind>('rehearsal');
  /* The window the figures are over. Three months is what every other figure in
     this app uses, so the table opens on the same question the rest of it
     answers; the other two are asked for by name. */
  const [months, setMonths] = useState<MatrixMonths>(3);
  const [pickingPeriod, setPickingPeriod] = useState(false);
  /* Which cell is open for editing, as the pair that identifies it. Not the
     cell's own coordinates: the grid is reloaded under the sheet after a save,
     and an index would then point at whatever moved into that place. */
  const [editing, setEditing] = useState<{ member: MatrixMember; session: MatrixSession } | null>(
    null,
  );
  const [saving, setSaving] = useState(false);
  /* The export, as three states and no fourth: idle, in flight, and the sentence
     saying where the file went. A failure replaces that sentence rather than
     joining it — «отправил» and «не отправил» on screen at once is a question
     the person cannot answer. */
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendFailure, setSendFailure] = useState<string | null>(null);
  const [editFailure, setEditFailure] = useState<string | null>(null);
  /* The strip of dates and the grid are two scrollers over the same columns,
     and the grid is the one a finger drags. A ref rather than state: a scroll
     event carries a pixel offset, and putting that through React would rebuild
     forty rows of cells for every frame of a drag. */
  const axisRef = useRef<HTMLDivElement>(null);

  function syncAxis(event: UIEvent<HTMLDivElement>): void {
    const axis = axisRef.current;
    if (axis !== null) axis.scrollLeft = event.currentTarget.scrollLeft;
  }

  const load = useCallback(async () => {
    // Cleared first: without it the previous kind's grid stays on screen
    // under the new heading, which reads as the numbers having changed.
    setMatrix(null);
    setFailure(null);
    try {
      setMatrix(await api.choirMatrix(kind, months));
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'Не удалось загрузить посещаемость');
    }
  }, [kind, months]);

  useEffect(() => {
    void load();
  }, [load]);

  /* A reload that keeps what is on screen. `load` clears the grid first, which
     is right when the question changes and wrong after an edit: the answer is
     the same table with one cell different, and blanking it reads as the whole
     thing having been recomputed. */
  const refresh = useCallback(async () => {
    try {
      setMatrix(await api.choirMatrix(kind, months));
    } catch {
      // The grid on screen is still true apart from the cell just written, and
      // the sheet says so. A refusal here would be about the reload, not about
      // the edit, and there is nothing for a person to do with it.
    }
  }, [kind, months]);

  /* The period the table is showing is the period exported: the person has
     already chosen it up there, and a second choice in the button would be the
     same question asked twice with two ways to disagree. */
  async function send(): Promise<void> {
    setSending(true);
    setSendFailure(null);
    setSent(false);
    try {
      await api.sendJournal(months);
      setSent(true);
    } catch (error) {
      setSendFailure(
        error instanceof ApiError ? error.message : 'Не удалось отправить таблицу',
      );
    } finally {
      setSending(false);
    }
  }

  async function setCell(status: AttendanceStatus): Promise<void> {
    if (editing === null) return;
    setSaving(true);
    setEditFailure(null);
    try {
      await api.markManually({
        sessionId: editing.session.session_id,
        memberId: editing.member.member_id,
        status,
      });
      setEditing(null);
      await refresh();
    } catch (error) {
      setEditFailure(error instanceof ApiError ? error.message : 'Не удалось изменить отметку');
    } finally {
      setSaving(false);
    }
  }

  const totalAttended = (matrix?.members ?? []).reduce((sum, row) => sum + row.attended, 0);
  const totalTotal = (matrix?.members ?? []).reduce((sum, row) => sum + row.total, 0);
  const totalLate = (matrix?.members ?? []).reduce((sum, row) => sum + row.late, 0);
  const totalExcused = (matrix?.members ?? []).reduce((sum, row) => sum + row.excused_absences, 0);
  const barred = (matrix?.members ?? []).filter((row) => row.barred).length;
  /* One label per run of columns in the same month, and a break of the same
     width in every row under it so the days keep standing over their cells.
     Empty when the window holds one month. */
  const axisMonths = monthLabels((matrix?.sessions ?? []).map((session) => session.start));
  /* The pinned block is the name and the «ПД» column standing as one, so it
     narrows by that column's width where the rule says nothing - otherwise the
     grid would start 24px in from an empty strip nothing explains. */
  const pinClass = matrix?.rule_in_force ? 'matrix-pin' : 'matrix-pin matrix-pin--no-streak';

  return (
    <>
      <div className="block">
        {/* The caption and the window it is over, on one line: the figures on
            the left are what the window changes, so the control that changes it
            stands at the end of the sentence they are in. The word is a control
            rather than a button because it says a value as well as offering to
            change it — «за 3 месяца ▾» reads as both. */}
        <div className="split">
          {matrix !== null && matrix.sessions.length > 0 ? (
            <p className="matrix-caption">
              <span className="num">{matrix.sessions.length}</span>{' '}
              {plural(matrix.sessions.length, [
                words(kind).nominative,
                words(kind).genitive,
                words(kind).genitivePlural,
              ])}{' '}
              · <span className="num">{matrix.members.length}</span>{' '}
              {plural(matrix.members.length, ['человек', 'человека', 'человек'])} · с{' '}
              {formatLongDate(matrix.period_start)}
            </p>
          ) : (
            <span />
          )}
          <button
            className="label-action"
            data-press="fade"
            aria-expanded={pickingPeriod}
            onClick={() => setPickingPeriod(!pickingPeriod)}
          >
            {/* One chevron turned over, and not «▾» swapped for «▴». The button
                survives the fold, so the glyph in it is the same element on
                both sides and can be animated - which two characters cannot be,
                and which is the rule every other fold in the app already keeps.
                The value is keyed on itself so that a period the person has just
                chosen arrives rather than appearing. */}
            <span className="label-value" key={months}>
              за {PERIODS.find(([value]) => value === months)?.[1]}
            </span>
            <ChevronDownIcon size={16} quiet turned={pickingPeriod} />
          </button>
        </div>
        {/* The second segment on this screen, and it asks a different question
            from the first: that one chose the table over the list of what has
            happened, this one chooses which gatherings the table counts. */}
        <div className="seg">
          <button
            className={kind === 'rehearsal' ? 'btn btn--selected' : 'btn btn--outline'}
            onClick={() => setKind('rehearsal')}
          >
            Спевки
          </button>
          <button
            className={kind === 'service' ? 'btn btn--selected' : 'btn btn--outline'}
            onClick={() => setKind('service')}
          >
            Участия
          </button>
        </div>

        {/* Under the kind rather than over it, at the owner's word of
            2026-08-24. Both are segments and they were arriving one above the
            other, so the row that is always on screen came second while the row
            that appears on a tap pushed it down — the standing choice moving to
            make room for the passing one. It opens in place, which is the
            container this system gives a choice that changes nothing but what is
            on screen. */}
        <Unfold open={pickingPeriod}>
          <div className="seg">
            {PERIODS.map(([value, word]) => (
              <button
                key={value}
                className={months === value ? 'btn btn--selected' : 'btn btn--outline'}
                onClick={() => {
                  setMonths(value);
                  setPickingPeriod(false);
                }}
              >
                {word}
              </button>
            ))}
          </div>
        </Unfold>
      </div>

      {/* The two states that are not the grid, and neither of them is drawn
          once there is a grid: a switch between «Спевки» and «Участия» that
          fails leaves the table that is on screen alone. */}
      {matrix === null && failure === null && <Skeleton />}
      {matrix === null && failure !== null && (
        <Failed subject="attendance" onRetry={() => void load()} />
      )}

      {matrix !== null && matrix.sessions.length === 0 && (
        <Settling>
          <p className="hint">
            Закрытых {words(kind).genitivePlural} с {formatLongDate(matrix.period_start)} нет.
            Матрица появится после первой.
          </p>
        </Settling>
      )}

      {matrix !== null && matrix.sessions.length > 0 && (
        <Settling>
          <>
            {/* Four squares and four words. A grid of colour with no key is a
                picture rather than a table, and this is the one screen where a
                cell is too small to carry its own word. */}
            <div className="legend">
              {LEGEND.map(([status, label]) => (
                <span className="legend-item" key={status}>
                  <span className="cell" data-status={status} />
                  {label}
                </span>
              ))}
            </div>

            <div className="matrix-frame">
              {/* The axis of day numbers, in a row of its own over every part.

                  It lived in the first group's heading until 2026-08-28, which
                  put the strip over the sopranos and nothing over the alti, the
                  tenors and the basses: the numbers left the screen with
                  «СОПРАНО» on the first scroll down, and a red cell four rows
                  below it belonged to a date the regent had to count columns for.
                  A row that says nothing but «числа» was the price, and it is
                  smaller than a table without an axis for three parts of four.

                  It is outside the grid's scroller because it cannot be both
                  stuck and inside: `position: sticky` within a horizontal
                  scroller sticks to that scroller, which never moves vertically.
                  So the axis is a scroller of its own, told where to stand by the
                  grid's — `syncAxis` above. */}
              <div className="matrix-axis" ref={axisRef}>
                <div className="matrix-row matrix-row--axis">
                  <div className={pinClass}>
                    <div className="matrix-name" />
                    {matrix.rule_in_force && <div className="matrix-streak">ПД</div>}
                  </div>
                  <div className="matrix-cells">
                    {matrix.sessions.map((session, index) => (
                      <Fragment key={session.session_id}>
                        {axisMonths[index] !== null && (
                          <span className="matrix-break">{axisMonths[index]}</span>
                        )}
                        <span className="matrix-day">{formatDay(session.start)}</span>
                      </Fragment>
                    ))}
                  </div>
                </div>
              </div>
              <div className="matrix-scroll" onScroll={syncAxis}>
                {groups(matrix).map(({ part, people, totals }) => (
                  <Fragment key={part ?? 'none'}>
                    {/* The part's own row, on the one flat surface in the app:
                        a shelf rather than a card, because it heads the rows
                        under it instead of standing beside them. Every part
                        says the same three things now that the numbers have a
                        row of their own: the name, the count, the share. */}
                    <div className="matrix-row matrix-row--part">
                      {/* The name and the run of misses beside it are one
                          pinned block, so the column travels with the names on
                          any horizontal scroll — the handoff's own words. The
                          prototype leaves the column loose, which a still
                          picture cannot tell apart from this. A part shelf
                          leaves it empty. */}
                      <div className={pinClass}>
                        <div className="matrix-name">
                          {voicePartName(part).toUpperCase()} · {people.length}
                          {totals !== undefined && (
                            <> · {share(totals.attended, totals.total)}</>
                          )}
                        </div>
                        {matrix.rule_in_force && <div className="matrix-streak" />}
                      </div>
                    </div>
                    {people.map((member) => (
                      <div
                        className={
                          member.member_id === myMemberId
                            ? 'matrix-row matrix-row--mine'
                            : 'matrix-row'
                        }
                        key={member.member_id}
                      >
                        <div className={pinClass}>
                          <div className="matrix-name">
                            {/* «Ты» and not the name: in a grid read for one's
                                own line, the name is the one thing the reader
                                already knows. */}
                            <span className="matrix-who">
                              {member.member_id === myMemberId ? 'Ты' : member.display_name}
                            </span>
                            <span className="matrix-sum">
                              {member.attended}/{member.total}
                              {member.late > 0 && <> · {member.late} оп</>}
                            </span>
                          </div>
                          {matrix.rule_in_force && (
                            <div
                              className={
                                member.barred
                                  ? 'matrix-streak matrix-streak--barred'
                                  : 'matrix-streak'
                              }
                            >
                              {/* Red at two and above and quiet below it. A `1`
                                  stays quiet on purpose: every spring half the
                                  choir has one, and a matrix that is half red
                                  says nothing at all. */}
                              {member.misses_streak === 0 ? '—' : member.misses_streak}
                            </div>
                          )}
                        </div>
                        <div className="matrix-cells">
                          {member.cells.map((cell, index) => {
                            const session = matrix.sessions[index];
                            return (
                              <Fragment key={session.session_id}>
                                {/* The month's own break, empty here: the strip
                                    above names the month over it, and the row
                                    carries the same gap so the two stay in
                                    step. */}
                                {axisMonths[index] !== null && <span className="matrix-break" />}
                                {/* A cell is a control only where the server says
                                    an edit would be accepted — a regent, inside
                                    the fourteen days. For everybody else it stays
                                    what it has always been: a square of colour
                                    that does not answer a press. */}
                                {!session.can_edit ? (
                                  <span className="cell" data-status={cell ?? 'none'} />
                                ) : (
                                  <button
                                    className="cell cell--editable"
                                    data-press="fade"
                                    data-status={cell ?? 'none'}
                                    aria-label={`${member.display_name}, ${formatLongDate(session.start)}`}
                                    onClick={() => {
                                      setEditFailure(null);
                                      setEditing({ member, session });
                                    }}
                                  />
                                )}
                              </Fragment>
                            );
                          })}
                        </div>
                      </div>
                  ))}
                  </Fragment>
                ))}
              </div>
              {/* Says the grid continues without taking a tap that was meant for
                  a cell under it. */}
              <span className="matrix-fade" />
            </div>

            <p className="matrix-hint">
              {matrix.rule_in_force && 'ПД — пропуски подряд · 2 и больше: на участие не идёт · '}←
              тяни таблицу, чтобы уйти в прошлое · пустая клетка — человека ещё не было в хоре
            </p>

            {/* The same two monospaced lines the singer's «Хор в цифрах» uses, on
                the reasoning that they answer the same question - how is the
                choir doing - and should not be two different objects. */}
            <section className="figures">
              <h2 className="figures-title">Итоги периода</h2>
              <p className="figures-line">
                средняя {share(totalAttended, totalTotal)} · опозданий{' '}
                <span className="num">{totalLate}</span> · уважительных{' '}
                <span className="num">{totalExcused}</span>
              </p>
              <p className="figures-line">
                {matrix.by_voice_part
                  .map((part) => `${voicePartLetter(part.voice_part)} ${share(part.attended, part.total)}`)
                  .join(' · ')}
              </p>
              {/* The rule's own total, and only when it has somebody to count:
                  «на участие не идут 0» is a line that says nothing every week of
                  a choir that turns up. */}
              {barred > 0 && (
                <p className="figures-line">
                  на участие не идут <span className="num">{barred}</span>
                </p>
              )}
            </section>

            {/* The regent's only, and under the figures rather than beside the
                period: it takes away what is on screen, so it comes after the
                screen has said what that is. The word says where the file goes —
                a button labelled «выгрузить» would promise a download, and this
                one sends a message. */}
            {isRegent && (
              <div className="block">
                <button
                  className="btn btn--outline"
                  disabled={sending}
                  onClick={() => void send()}
                >
                  {sending ? 'Отправляю…' : 'Прислать таблицу в чат'}
                </button>
                {sent && !sending && (
                  <p className="hint">
                    Файл в чате с ботом. Открывается в Excel и в Google Таблицах.
                  </p>
                )}
                {sendFailure !== null && <p className="failure">{sendFailure}</p>}
              </div>
            )}
          </>
        </Settling>
      )}

      {/* Whose cell was tapped, said before anything can be changed. A cell is
          16px in a grid that cannot give it the 44 this system asks of a
          target, so a miss is possible in a way it is nowhere else in the app —
          and the answer to that is not a bigger cell, which the grid has no
          room for, but a sheet that names the person and the date and writes
          nothing until a verdict is chosen. Closing it costs nothing. */}
      <Sheet
        open={editing !== null}
        label="Отметка"
        onClose={() => {
          setEditing(null);
          setEditFailure(null);
        }}
      >
        {editing !== null && (
          <>
            <p className="date-line">{formatLongDate(editing.session.start)}</p>
            <h2 className="card-title">{editing.member.display_name}</h2>
            <div className="seg">
              {VERDICTS.map(([status, label]) => (
                <button
                  key={status}
                  className={
                    editing.member.cells[
                      matrix?.sessions.findIndex(
                        (column) => column.session_id === editing.session.session_id,
                      ) ?? -1
                    ] === status
                      ? 'btn btn--selected'
                      : 'btn btn--outline'
                  }
                  disabled={saving}
                  onClick={() => void setCell(status)}
                >
                  {label}
                </button>
              ))}
            </div>
            {editFailure !== null && <p className="failure">{editFailure}</p>}
            {/* What the sheet cannot do, said rather than hidden: the minutes
                and the excuse live on the gathering's own screen, where the
                roster is a list of names rather than a column of squares. */}
            <p className="hint">
              Минуты опоздания и уважительность — на экране самой{' '}
              {words(kind).genitive}.
            </p>
          </>
        )}
      </Sheet>
    </>
  );
}

/**
 * The choir as a group: who is in it, and how it has been coming.
 *
 * Open to both roles since 2026-08-22, which is the audit's §8. A singer needs
 * two things they had no way to see: who is in the choir, and their own
 * attendance beside everybody else's — because the two-rehearsal rule is about
 * them, and the «ПД» column is where the warning on «Расписание» comes from.
 * The tabs stop jumping with it: «Я» is the third for both roles.
 *
 * What a singer does not get is everything that is a regent's record about
 * people: the «…» on a roster row, the filter by voice part, and the invite
 * link — which moved to «Я», where a singer never sees it.
 *
 * The hub is one page shorter as well. «Что уже прошло» was a page here and is
 * the tail of the schedule now: the same cards, only behind today rather than
 * ahead of it. The table is the one screen left that is pushed rather than
 * scrolled to, and it takes the client's own back key.
 */
export default function ChoirScreen({
  choir,
  home,
  supportContact,
}: {
  choir: ChoirBrief;
  /** The lit tab, tapped again. */
  home: number;
  /**
   * Whom to write to when the choir has lost its head regent. Passed through
   * rather than fetched: it arrives with the auth response `App` already holds,
   * and a second request for one string would be a request for nothing.
   */
  supportContact: string;
}) {
  const isRegent = choir.role === 'regent';
  const [page, setPage] = useState<Page>('choir');
  // The hub is the top of this tab, and every other page is pushed over it.
  // `person` rides with the page, so putting the page back puts it back too.
  useHome(home, () => setPage('choir'));
  /** How many closed gatherings nobody has confirmed. Shown on the way in. */
  const [needsReview, setNeedsReview] = useState(0);
  /** The counters on the two other tiles. */
  const [songCount, setSongCount] = useState(0);
  const [liveMessages, setLiveMessages] = useState(0);
  /**
   * Who keeps each of the two functions, by name.
   *
   * Computed from the roster this screen is about to draw anyway rather than
   * asked for: the flags are on every row, and a second request for two names
   * would be a second thing to keep in step with the first.
   */
  const [keepers, setKeepers] = useState<{ program: string; sermons: string } | null>(null);
  /** Whose card is open. Set with the page, so leaving it clears both. */
  const [person, setPerson] = useState<number | null>(null);
  /** What this reader may do to people, and whether he is the head. */
  const [mine, setMine] = useState<{ canManage: boolean; isHead: boolean } | null>(null);
  /**
   * The roster as this screen last read it. Kept whole rather than reduced to
   * the two or three numbers drawn from it, because the person's card needs one
   * more: how many regents the choir has besides the one whose card is open.
   */
  const [roster, setRoster] = useState<MemberRow[]>([]);
  /**
   * The invite code a deletion issued, and it lives until this screen is
   * reloaded rather than for ever: the code's own place is `InviteCard` on «Я»,
   * and it is already the new one there. What this band is for is the half a
   * minute in which nobody knows the old link has died.
   */
  const [rotated, setRotated] = useState<string | null>(null);
  /**
   * What has just been done and can still be taken back.
   *
   * It lives on the screen rather than in the card that did it: one bar between
   * the cards is the whole difference between a report and two of them stacked
   * over the tab bar.
   */
  const [done, setDone] = useState<Done | null>(null);
  /** A number per action, so a second one restarts the five seconds. */
  const acts = useRef(0);
  const report = useCallback((text: string, undo: () => void) => {
    acts.current += 1;
    setDone({ id: acts.current, text, undo });
  }, []);

  useEffect(() => {
    if (!isRegent) return;
    async function count(): Promise<void> {
      try {
        // The server's own count, over the whole history rather than one
        // page of it: needingReview() on a loaded page would undercount the
        // moment the history stopped fitting on the first one.
        // (Ours.) The server counts fourteen days (`backend/app/api/sessions.py:1464-1474`),
        // not the whole history. `FND-511`, `KEEP`, «Только комментарий; число считает сервер».
        setNeedsReview((await api.history()).needs_review_count);
      } catch {
        // A badge that cannot be counted is simply not drawn.
      }
    }
    void count();
  }, [isRegent, page]);

  // Everything the three tiles print. Re-read on the way back from any of them,
  // because that is exactly when a number on one of them has changed.
  //
  // (Ours.) After D1 six tiles are drawn — F-020's two, F-024's «Программа», F-023's «Песни
  // хора», F-026's «Сообщения от бота» and F-025's «Проповеди» — and all three requests are here:
  // the service ahead, for the programme's tile; the songs, for the count on theirs; and the
  // roster — the statistics tile reads `paused` off it, «Проповеди» the keepers. The messages half
  // under them (`:745-752`) is legacy's, since F-026. Asked for on arrival and again on every
  // return to the hub, as before (`:722-723`). The roster stays a request of its own beside the
  // roster card's: `FND-510`, `KEEP`, «чтобы при сборке никто молча не схлопнул запросы».
  // C-013 took the service ahead and its tile out, and C-017 the statistics tile and `paused`.
  useEffect(() => {
    if (page !== 'choir') return;
    async function doors(): Promise<void> {
      const [songs, members] = await Promise.allSettled([
        api.choirSongs(),
        api.choirMembers(),
      ]);
      if (songs.status === 'fulfilled') setSongCount(songs.value.length);
      if (members.status === 'fulfilled') {
        setRoster(members.value);
        setKeepers(whoKeeps(members.value));
        // Read off the reader's own row rather than told by a field of its own,
        // exactly as the roster does it: the head regent always, a second
        // regent only while the head trusts him with people.
        const me = members.value.find((row) => row.member_id === choir.member_id) ?? null;
        setMine({
          isHead: me?.is_head_regent === true,
          canManage: me?.role === 'regent' && (me.is_head_regent || me.is_roster_admin),
        });
      }
      // The sendings are the regent's, and asking for them as a singer is a
      // 403 the screen would have nothing to do with.
      if (!isRegent) return;
      try {
        setLiveMessages((await api.choirMessages()).filter((one) => one.enabled).length);
      } catch {
        // A tile whose count cannot be read draws a zero and still opens.
      }
    }
    void doors();
  }, [choir.member_id, isRegent, page]);

  // The table of attendance is the one page of this tab drawn here rather than
  // by a component of its own, so it is the one whose key is held here. The
  // other six are screens that hold their own.
  useBackKey(page === 'attendance' ? () => setPage('choir') : null);

  // A report belongs to the screen it was raised on, and leaving that screen
  // ends it. Both the card and the list carry the bar now, and the state is one
  // for the two: without this, going back inside the five seconds mounts a
  // second bar on the list and starts the count again, so one act is reported
  // twice. «Один бар и никогда два» is the rule it would break.
  useEffect(() => {
    setDone(null);
  }, [page]);

  /**
   * Whichever page of this tab is on screen, over the hub it was opened from.
   *
   * Every branch below is wrapped, and by the same element in the same place in
   * the tree: that is what lets `Push` keep the page being left mounted while it
   * slides out rather than draw it again from an empty state. The name is
   * passed rather than read off `page` inside the helper because a `program`
   * with no service falls through to the hub, and what animates has to be what
   * is drawn.
   */
  const pushed = (at: string, screen: ReactNode): ReactNode => (
    <Push at={at} depth={at === 'choir' ? 0 : 1}>
      {screen}
    </Push>
  );

  if (page === 'person' && person !== null) {
    return pushed(
      'person',
      <>
        <PersonScreen
          // Keyed by the person: opening a second card while the first is on
          // screen would otherwise reuse the instance and show the old figures
          // under the new name until the request came back.
          key={person}
          memberId={person}
          canManage={mine?.canManage === true}
          isHead={mine?.isHead === true}
          // Regents **besides** this one, counted here where the roster is in
          // hand. Not the choir's total: the card's own «Снять роль регента»
          // changes whether the person on it is a regent, and a total would go
          // stale on the very tap it guards — appointing somebody made the
          // button that undoes it disappear.
          otherRegents={
            roster.filter(
              (row) =>
                row.standing === 'active' && row.role === 'regent' && row.member_id !== person,
            ).length
          }
          onBack={() => setPage('choir')}
          onGone={(code) => {
            // There is no card any more, so «Хор» takes over — carrying the new
            // invite code, because the deletion rotated it and the choir has to
            // be told before anybody taps a link that has died.
            setRotated(code);
            setPage('choir');
          }}
          onDone={report}
        />
        {/* The card raises four reports of its own — the role both ways, the
            two functions, and bringing somebody back — and until this line it
            raised them into a bar that was mounted only on the list behind it.
            Nothing appeared on the tap, and the rule of 2026-08-21 wants one of
            two things from every act: a question before, or «Отменить» after.
            The card was giving neither. */}
        <UndoBar done={done} onGone={() => setDone(null)} />
      </>,
    );
  }

  if (page === 'sermons') {
    return pushed('sermons', <SermonsScreen onBack={() => setPage('choir')} />);
  }

  if (page === 'songs') {
    return pushed('songs', <SongsScreen isRegent={isRegent} onBack={() => setPage('choir')} />);
  }

  if (page === 'messages') {
    return pushed('messages', <MessagesScreen onBack={() => setPage('choir')} />);
  }

  if (page === 'attendance') {
    return pushed(
      'attendance',
      <>
        <div className="screen-head">
          <h1 className="screen-title">Посещаемость</h1>
        </div>
        {/* One segment where there were two. The other one chose between the
            table and the list of what had happened, and that list is the tail
            of the schedule now. */}
        <MatrixPanel myMemberId={choir.member_id} isRegent={isRegent} />
      </>,
    );
  }

  return pushed(
    'choir',
    <>
      {/* A link that has died, said where the person who killed it is standing.
          Deleting somebody rotates the code in the same transaction — it is the
          only way to shut the door on a person the app has forgotten — and
          until the choir is sent the new one, everybody holding the old link
          has a dead link and no way of knowing.

          It lives until this screen is reloaded and no longer: the code's own
          place is «Ссылка-приглашение» on «Я», where it is already the new one.
          This is the report, not the record. */}
      {rotated !== null && (
        <section className="card card--toned">
          <span className="label label--tone">Ссылка хора сменилась</span>
          <p className="invite-code">{rotated}</p>
          <p className="hint">Старая перестала работать у всех — разошли новую хору.</p>
        </section>
      )}

      {/* Who keeps each of the two functions, read by the whole choir. It is a
          fact about the choir the way the roster's «регент» is, and a singer
          wondering whom to ask about Sunday's order should not have to open a
          menu they do not have. Where nobody has been appointed it names the
          regent, because that is what «не поручил — делает регент» looks like
          to somebody reading it. */}
      {/* C-023 batch 2 (`FND-1618`): the keepers come with the roster's answer, after the hub
          has been drawn, and arriving whole the line pushed everything under it down in one
          frame (31 px on the audit's rig). It grows in instead. */}
      <Unfold open={keepers !== null}>
        <p className="hint">
          Программу ведёт {keepers?.program} · проповеди ставит {keepers?.sermons}.
        </p>
      </Unfold>

      {/* One door into attendance, and it is the first thing on the screen: for
          the regent it is what he comes to this tab to act on, and for a singer
          it is the one place their own record stands beside everybody's. */}
      <button className="tile" onClick={() => setPage('attendance')}>
        <GridIcon size={21} />
        <span className="tile-body">
          <span className="tile-title">Таблица посещаемости</span>
          <span className="tile-sub tile-sub--wrap">кто и как ходил, по именам</span>
        </span>
        {needsReview > 0 && (
          <span className="answer" data-tone="late">
            <span className="num">{needsReview}</span>
          </span>
        )}
        <ChevronRightIcon size={20} quiet />
      </button>

      {/* Two doors into what the regent has been doing by hand, and they are
          drawn for everybody: the repertoire is a fact about the choir, and
          «Сообщения» is the one that is the regent's alone.

          C-013 (`FND-1608`, 2026-10-07, after the cutover): the third, «Программа», went at the
          owner's word. It led to the next service's programme, which that service's own card
          opens, so it was a second road to one screen. */}
      <button className="tile" onClick={() => setPage('songs')}>
        <span className="tile-body">
          <span className="tile-title">Песни хора</span>
          <span className="tile-sub tile-sub--wrap">
            что хор разучил — из этого списка регент ставит номера
          </span>
        </span>
        <span className="tile-count">{songCount}</span>
        <ChevronRightIcon size={20} quiet />
      </button>

      {isRegent && (
        <button className="tile" onClick={() => setPage('messages')}>
          <span className="tile-body">
            <span className="tile-title">Сообщения от бота</span>
            <span className="tile-sub tile-sub--wrap">что и когда бот пишет каждому в личку</span>
          </span>
          <span className="tile-count">{liveMessages}</span>
          <ChevronRightIcon size={20} quiet />
        </button>
      )}

      {/* The list is the regent's and the appointed brother's to edit, and
          nobody else has anything to do on it: who prepares this week stands on
          the evening's own screen. The keeper sees the door too since C-002
          (`FND-1591`, reversing `FND-929` for this door). */}
      {/* No icon before it, and that is this file's own rule rather than an
          omission: every glyph in the set already names a tab, and one of them
          repeated inside the tab it names says nothing. */}
      {(isRegent || choir.is_sermon_admin) && (
        <button className="tile" onClick={() => setPage('sermons')}>
          <span className="tile-body">
            <span className="tile-title">Проповеди</span>
            <span className="tile-sub tile-sub--wrap">
              готовят четверо по очереди · ведёт {keepers?.sermons ?? 'регент'}
            </span>
          </span>
          <ChevronRightIcon size={20} quiet />
        </button>
      )}

      {/* No «Хор» over a tab called «Хор», under a header carrying the choir's
          own name: the bar says where you are, and a screen title that repeats
          it is a third label for one thing. */}
      <MembersCard
        myMemberId={choir.member_id}
        readOnly={!isRegent}
        supportContact={supportContact}
        onOpen={(memberId) => {
          setPerson(memberId);
          setPage('person');
        }}
        onDone={report}
      />

      <UndoBar done={done} onGone={() => setDone(null)} />
    </>,
  );
}
